import React, { useMemo } from 'react';
import { FormattedAIResponse } from './FormattedAIResponse';
import { SearchResults } from './search/SearchResults';
import { GoogleAIRichResults } from './search/GoogleAIRichResults';
import { ParallelSearchResponse } from './ParallelSearchResponse';
import { LARAResultsRenderer } from './agents/LARAResultsRenderer';
import { WikipediaCard } from './agents/WikipediaCard';
import { PPTMakerViewer } from './agents/PPTMakerViewer';
import { FlowChartRenderer } from './agents/FlowChartRenderer';
import { ColorPaletteResponse } from './agents/ColorPaletteResponse';
import { MemeRenderer } from './meme/MemeComponents';
import { VGenVideoStatus } from './agents/VGenVideoStatus';
import { VideoSummaryCard } from './agents/VideoSummaryCard';
import { YouTubeResults } from './agents/YouTubeResults';
import { GiphyResults } from './agents/GiphyResults';
import { PasswordGeneratorCard } from './agents/PasswordGeneratorCard';
import { QRCodeGeneratorCard } from './agents/QRCodeGeneratorCard';
import { CalculatorCard } from './agents/CalculatorCard';
import { TranslatorCard } from './agents/TranslatorCard';
import { UnitConverterCard } from './agents/UnitConverterCard';
import WeatherCard from './WeatherCard';
import chatgptLight from '@/assets/chatgpt_light.png';
import chatgptDark from '@/assets/chatgpt_dark.png';
import deepseekLogo from '@/assets/deepseek.png';
import claudeLogo from '@/assets/claude.png';
import geminiLogo from '@/assets/gemini.png';
import llamaLogo from '@/assets/llama.png';
import qwenLogo from '@/assets/qwen.png';
import ogaiLogo from '/image.png';

// Helper to clean think tags from content
const cleanThinkTags = (s: unknown) => {
    if (typeof s !== "string") return "";
    return s.replace(/<think>[\s\S]*?<\/think>/gi, "").trim();
};

const parseGoogleAiPayload = (content: unknown) => {
    if (content && typeof content === 'object') {
        const obj = content as any;
        return obj?.__googleai ? obj : null;
    }
    if (typeof content !== "string") return null;
    const raw = content.trim();
    if (!raw) return null;
    try {
        const parsed = raw.startsWith('{') ? JSON.parse(raw) : JSON.parse(raw.replace(/^```json\s*/i, '').replace(/```\s*$/i, ''));
        if (parsed && parsed.__googleai) return parsed;
    } catch { }
    return null;
};

const getModelLogo = (model?: string, isDarkMode?: boolean): string => {
    const key = model?.toLowerCase();
    switch (key) {
        case 'gpt':
        case 'chat gpt':
        case 'chatgpt':
        case 'glm':
            return isDarkMode ? chatgptLight : chatgptDark;
        case 'deepseek':
            return deepseekLogo;
        case 'claude':
            return claudeLogo;
        case 'gemini':
            return geminiLogo;
        case 'llama':
            return llamaLogo;
        case 'qwen':
            return qwenLogo;
        case 'lara':
            return '/ai-svg-oreog.svg';
        default:
            return ogaiLogo;
    }
};

const getDisplayName = (model: string, agentName?: string): string => {
    if (agentName && !agentName.toLowerCase().includes('suffix') && !agentName.toLowerCase().includes('agent')) {
        return agentName;
    }
    const modelDisplayNames: { [key: string]: string } = {
        'gpt': 'ChatGPT',
        'chat gpt': 'ChatGPT',
        'chatgpt': 'ChatGPT',
        'claude': 'Claude',
        'gemini': 'Gemini',
        'llama': 'LLaMA',
        'deepseek': 'DeepSeek',
        'qwen': 'Qwen',
        'perplexity': 'Perplexity',
        'google ai': 'Google AI',
        'googleai': 'Google AI',
        'gpt-oss': 'GPT-OSS',
        'color-palette': 'Color Palette',
        'pixabay': 'Pixabay',
        'weather': 'Weather',
        'web-search': 'Web Search',
        'web search': 'Web Search',
        'lara': 'LARA'
    };
    return modelDisplayNames[model?.toLowerCase()] || model || 'AI Assistant';
};

interface ChatPdfRendererProps {
    messages: any[];
    title: string;
    userName?: string;
}

export const ChatPdfRenderer: React.FC<ChatPdfRendererProps> = ({ messages, title, userName = "User" }) => {
    // Force light mode for PDF usually looks better for printing, but we can respect dark mode if we want.
    // Generally PDFs are better in light mode to save ink/toner if user prints, but for digital PDF dark is fine.
    // The Sidebar passed `dark` param to print view, so let's stick to light mode for standard "Document" feel,
    // OR we can make it an option. For now, let's hardcode light mode for 100% clean PDF look unless requested otherwise.
    // EDIT: User apps often look better in light mode for PDF.
    const isDarkMode = false;

    const renderAssistantContent = (m: any) => {
        // 1. Check for specific Agent types first

        // LARA
        if (m.isLARA && m.laraResults) {
            return <LARAResultsRenderer results={m.laraResults} isDarkMode={isDarkMode} />;
        }

        // Agent Data Handling
        const agentData = m.agentData || {};
        const selectedAgent = m.selectedAgent;

        // PPT
        if (agentData.ppt) {
            // PPTViewer handles its own rendering. We might need to ensure it's fully expanded/visible.
            return <PPTMakerViewer ppt={agentData.ppt} isDarkMode={isDarkMode} />;
        }

        // Flowchart
        if (agentData.flowchart) {
            return <FlowChartRenderer spec={agentData.flowchart} isDarkMode={isDarkMode} />;
        }

        // Weather
        if (m.weatherData) {
            // Re-construct weather data object if needed, or pass as is if compatible
            // Using a simplified check since WeatherCard expects specific structure
            return <WeatherCard data={m.weatherData} />;
        }

        // Wikipedia
        if (agentData.wikipedia) {
            return <WikipediaCard data={agentData.wikipedia} isDarkMode={isDarkMode} />;
        }

        // Video Summary
        if (agentData.videoId && agentData.summary) {
            return <VideoSummaryCard result={agentData} isDarkMode={isDarkMode} />;
        }

        // YouTube specific handling via agent name or content structure
        if (selectedAgent?.name === "YouTube Search" || agentData.youtubeResults) {
            // Check if we have results in content or agentData
            let results = agentData.youtubeResults;
            if (!results && typeof m.content === 'string' && m.content.startsWith('{')) {
                try {
                    const parsed = JSON.parse(m.content);
                    if (parsed.youtubeResults) results = parsed.youtubeResults;
                } catch { }
            }
            if (results) {
                // Parse query from content or default
                let query = "YouTube Search";
                try {
                    if (m.agentData?.query) query = m.agentData.query;
                    else if (typeof m.content === 'string' && m.content.startsWith('{')) {
                        const p = JSON.parse(m.content);
                        if (p.query) query = p.query;
                    }
                } catch { }

                return <YouTubeResults data={results} query={query} isDarkMode={isDarkMode} />;
            }
        }

        // Giphy
        if (selectedAgent?.name === "GIF Search" || agentData.giphyResults) {
            let results = agentData.giphyResults;
            // fallback parsing logic if needed
            if (results) {
                return <GiphyResults gifs={results} isDarkMode={isDarkMode} />;
            }
        }

        // Meme
        if (selectedAgent?.name === "Meme Generator" || agentData.meme) {
            // If the meme is already generated (imageUrl), display it
            if (agentData.meme && agentData.meme.url) {
                return (
                    <div className="flex justify-center my-4">
                        <img src={agentData.meme.url} alt="Generated Meme" className="max-w-full rounded-lg border shadow-sm" style={{ maxHeight: '500px' }} />
                    </div>
                );
            }
            // If it's a template list
            if (Array.isArray(m.memeTemplates)) {
                // We can render a grid of templates or just a summary
                // For PDF, maybe just show the first few? Or skip if it's just selection UI.
                // Let's render the MemeRenderer components if content is JSON
            }
        }

        // Attempt standard JSON parsing for agents that stick data in content
        let parsedContent: any = null;
        try {
            if (typeof m.content === 'string' && (m.content.trim().startsWith('{') || m.content.trim().startsWith('['))) {
                parsedContent = JSON.parse(m.content);
            }
        } catch { }

        // Color Palette
        if (selectedAgent === "color-palette" || (parsedContent && parsedContent.colors && parsedContent.paletteName)) {
            return <ColorPaletteResponse palette={parsedContent || m.content} isDarkMode={isDarkMode} />;
        }

        // Password Generator
        if (parsedContent && parsedContent.password && parsedContent.strength) {
            return <PasswordGeneratorCard initialPassword={parsedContent.password} isDarkMode={isDarkMode} />;
        }

        // QR Code
        if (parsedContent && parsedContent.qrCodeUrl) {
            return <QRCodeGeneratorCard text={parsedContent.text} qrUrl={parsedContent.qrCodeUrl} isDarkMode={isDarkMode} />;
        }

        // Calculator
        if (parsedContent && parsedContent.expression && parsedContent.result) {
            return <CalculatorCard
                data={{
                    expression: parsedContent.expression,
                    result: parsedContent.result,
                    explanation: parsedContent.explanation
                }}
                isDarkMode={isDarkMode}
            />;
        }

        // Translator
        if (parsedContent && parsedContent.translatedText && parsedContent.sourceLanguage) {
            return <TranslatorCard
                data={{
                    input: parsedContent.sourceText,
                    output: parsedContent.translatedText,
                    to: parsedContent.targetLanguage,
                    explanation: parsedContent.explanation
                }}
                isDarkMode={isDarkMode}
            />;
        }

        // Unit Converter
        if (parsedContent && parsedContent.inputValue && parsedContent.outputValue) {
            const inputStr = `${parsedContent.inputValue} ${parsedContent.inputUnit}`;
            const outputStr = `${parsedContent.outputValue} ${parsedContent.outputUnit}`;
            return <UnitConverterCard
                data={{
                    input: inputStr,
                    output: outputStr
                }}
                isDarkMode={isDarkMode}
            />;
        }

        // Google AI Rich Results
        const parsedGoogleAi = parseGoogleAiPayload(m.content);
        if (parsedGoogleAi) {
            const fromBlocks = (parsedGoogleAi?.data?.text_blocks && Array.isArray(parsedGoogleAi.data.text_blocks))
                ? parsedGoogleAi.data.text_blocks
                    .map((b: any) => (typeof b?.snippet === 'string' ? b.snippet : (typeof b?.text === 'string' ? b.text : '')))
                    .filter(Boolean)
                    .join("\n\n")
                : "";
            const text = String(fromBlocks || parsedGoogleAi?.text || "");

            return (
                <div className="space-y-3">
                    <FormattedAIResponse
                        content={text}
                        isStreaming={false}
                        isDarkMode={isDarkMode}
                    />
                    <GoogleAIRichResults data={parsedGoogleAi?.data || {}} isDarkMode={isDarkMode} />
                </div>
            );
        }

        // Deep Research
        if (m.model === "deep-research") {
            return (
                <ParallelSearchResponse
                    content={cleanThinkTags(m.content)}
                    isDarkMode={isDarkMode}
                    sources={(m.parallelSources || []) as any}
                />
            );
        }

        // Web Search Results
        if (m.searchResults) {
            return (
                <div className="space-y-3">
                    {typeof m.content === 'string' && m.content.trim() && (
                        <FormattedAIResponse
                            content={m.content}
                            isStreaming={false}
                            isDarkMode={isDarkMode}
                        />
                    )}
                    <SearchResults results={m.searchResults} isDarkMode={isDarkMode} renderMode="full" hideSourcesButton={true} />
                </div>
            );
        }

        // Standard Content
        return (
            <FormattedAIResponse
                content={typeof m.content === "string" ? m.content : JSON.stringify(m.content ?? "")}
                isStreaming={false}
                isDarkMode={isDarkMode}
            />
        );
    };

    return (
        <div id="chat-pdf-container" className={`p-8 max-w-[800px] mx-auto bg-white text-black min-h-screen`}>
            {/* PDF Header */}
            <div className="border-b pb-6 mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold mb-2">{title}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <img src="/oreog_icon_with_text.png" alt="Oreog" className="h-8 w-auto" />
                </div>
            </div>

            <div className="space-y-8">
                {messages.map((m, idx) => (
                    <div key={idx} className={`flex flex-col gap-1 ${m.role === 'user' ? 'items-end' : 'items-start'}`}>

                        {/* AI Header (Logo + Name) - Only for Assistant */}
                        {m.role !== 'user' && (
                            <div className="flex items-center gap-2 mb-1">
                                <img
                                    src={getModelLogo(m.model || m.displayModel, isDarkMode)}
                                    alt="AI"
                                    className="h-6 w-6 rounded-full object-cover"
                                />
                                <span className="font-semibold text-sm">
                                    {getDisplayName(m.displayModel || m.model, m.agentData?.agentName || m.selectedAgent?.name)}
                                </span>
                                <span className="text-xs text-gray-400">
                                    {m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                            </div>
                        )}

                        {/* Content Bubble */}
                        <div
                            className={`
                                max-w-[85%] rounded-2xl px-4 py-2 text-sm
                                ${m.role === 'user'
                                    ? (isDarkMode ? 'bg-[#202020] text-gray-100' : 'bg-gray-100 text-gray-900')
                                    : 'pl-0' /* AI content usually aligned left without bubble background in this specific renderer, or we can look at app. App AI uses transparent bg usually. */
                                }
                            `}
                        >
                            {m.role === 'user' ? (
                                <div className="whitespace-pre-wrap leading-relaxed">
                                    {typeof m.content === "string" ? m.content : JSON.stringify(m.content ?? "")}
                                    {/* Attachments */}
                                    {m.attachedMedia?.type === 'image' && m.attachedMedia?.url && (
                                        <div className="mt-2">
                                            <img src={m.attachedMedia.url} alt="User attachment" className="max-w-sm rounded-lg border" />
                                        </div>
                                    )}
                                    {Array.isArray(m.attachedMediaList) && m.attachedMediaList.length > 0 && (
                                        <div className="mt-2 grid grid-cols-3 gap-2 max-w-lg">
                                            {m.attachedMediaList.filter((x: any) => x?.type === 'image' && x?.url).map((x: any, i: number) => (
                                                <img key={i} src={x.url} alt="Attachment" className="w-full rounded-md border" />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-800'} leading-relaxed`}>
                                    {renderAssistantContent(m)}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className="mt-12 pt-6 text-center text-md font-semibold text-black">
                Oreog AI
            </div>
        </div>
    );
};
